import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenAI } from '@google/genai';

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm here to help you understand the South Asian matrix and guide you toward mental freedom. Ask me anything about breaking free from societal conditioning, overcoming fear, or finding your authentic path."
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const API_KEY = 'AIzaSyB26HA308zw-_6spUOgbsh2m7hegLeKqq8';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const suggestedQuestions = [
    "Why do I feel guilty saying no to my parents?",
    "How can I overcome fear of judgment?",
    "What if I fail when trying something new?",
    "How to deal with family pressure about career?",
    "Why do I feel stuck in the same routine?",
    "How can I become more creative and confident?"
  ];

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Initialize the Google GenAI client
      const ai = new GoogleGenAI({ apiKey: API_KEY });

      const prompt = `You are an empathetic AI mentor helping South Asian youth break free from societal conditioning and mental barriers.

**STRICT CONTEXT**: You are part of a psychology presentation titled "Breaking the South Asian Matrix" about:
- How centuries of colonialism, fear-based parenting, and rigid hierarchies created mental conditioning
- The "slave mentality" - people who get power treat others like slaves
- How this limits creativity and independent thinking
- How Gen Z can break this cycle through awareness

**YOUR ROLE**:
- ONLY answer questions related to: mental conditioning, breaking free from societal pressure, overcoming fear, finding independence, dealing with family/cultural pressure, building confidence, pursuing dreams
- If question is OFF-TOPIC (sports, weather, general knowledge, etc.), politely redirect: "I'm here specifically to help with breaking the South Asian matrix and mental freedom. Ask me about overcoming societal pressure, dealing with family expectations, or finding your independent path."
- Be empathetic but concise (3-4 sentences max)
- Provide actionable advice, not just theory
- Validate feelings while encouraging growth
- Reference psychology concepts when relevant (conformity bias, learned helplessness, etc.)

**USER'S QUESTION**: ${messageText}

**YOUR RESPONSE** (Keep it focused, practical, and under 100 words):`;

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: prompt,
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 200,
          topP: 0.8,
          topK: 40
        }
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.text
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble connecting right now. But remember: awareness is the first step to freedom. Keep questioning, keep growing. The matrix breaks when you start thinking independently."
      }]);
    }

    setIsLoading(false);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-primary to-accent text-white p-4 rounded-full shadow-2xl hover:shadow-primary/50 transition-all duration-300 transform hover:scale-110 animate-pulse group"
          aria-label="Open Chat"
        >
          <div className="relative">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {/* Notification Badge */}
            <span className="absolute -top-1 -right-1 bg-danger text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
              !
            </span>
          </div>
          <span className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 bg-darker text-white px-4 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
            Ask me anything!
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[600px] bg-gradient-to-br from-dark/95 to-darker/95 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-primary/30 flex flex-col animate-fadeIn">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-accent p-4 rounded-t-3xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-2xl">🪞</span>
              </div>
              <div>
                <h3 className="font-bold text-white">Matrix Mirror</h3>
                <p className="text-xs text-white/80">Your guide to freedom</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-4 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-primary to-secondary text-white rounded-br-sm'
                      : 'bg-dark/50 border border-accent/30 text-gray-200 rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-dark/50 border border-accent/30 p-4 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions (show only initially) */}
          {messages.length === 1 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.slice(0, 2).map((question, index) => (
                  <button
                    key={index}
                    onClick={() => sendMessage(question)}
                    className="text-xs bg-primary/20 hover:bg-primary/30 text-primary px-3 py-1.5 rounded-full transition-colors border border-primary/30"
                  >
                    {question.length > 30 ? question.substring(0, 30) + '...' : question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-primary/20">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isLoading && sendMessage()}
                placeholder="Ask about breaking the matrix..."
                className="flex-1 bg-darker/50 border border-primary/30 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-primary placeholder-gray-500"
                disabled={isLoading}
              />
              <button
                onClick={() => sendMessage()}
                disabled={isLoading || !input.trim()}
                className="bg-gradient-to-r from-primary to-accent text-white px-5 py-2 rounded-full font-semibold hover:shadow-lg hover:shadow-primary/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChat;
